import * as React from "react";
import { useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import {
  ButtonSection,
  Container,
  ContentSection,
  FormSection,
  HeaderSection,
  InputRow,
  PrimaryButtons,
} from "../../../../Common/__styles__/Modal";
import { Button } from "../../../../Common/Button";
import Modal from "../../../../Common/Modal";
import { Body, Title } from "../../../../Common/Typography";
import { Radio } from "../../../../Inputs/react-hook-form";

export interface PromoteSubmissionsModalHook {
  onSubmit: (data: { promoteSubmissions: string }) => void;
  onCancel: () => void;
  breakingChanges?: {
    updatedToRequiredFields: string[];
  };
}
export const usePromoteSubmissionsModal = ({
  onSubmit,
  onCancel,
  breakingChanges,
}: PromoteSubmissionsModalHook) => {
  const [showPromoteSubmissionsModal, hidePromoteSubmissionsModal] = useModal(
    () => (
      <Modal
        onRequestClose={() => {
          onCancel();
        }}
      >
        <PromoteSubmissionsForm
          onSubmit={onSubmit}
          onCancel={onCancel}
          breakingChanges={breakingChanges}
        />
      </Modal>
    ),
    [onSubmit, breakingChanges],
  );

  return [showPromoteSubmissionsModal, hidePromoteSubmissionsModal] as const;
};

const PromoteSubmissionsForm = ({
  onSubmit,
  onCancel,
  breakingChanges,
}: PromoteSubmissionsModalHook) => {
  const { control, handleSubmit } = useForm<{ promoteSubmissions: string }>({
    defaultValues: {
      promoteSubmissions: "false",
    },
  });

  const hasBreakingChanges =
    (breakingChanges?.updatedToRequiredFields?.length ?? 0) > 0;

  return (
    <Container overflows>
      <HeaderSection>
        <Title size="small" type="regular">
          Update existing records
        </Title>
      </HeaderSection>
      <FormSection overflows>
        <ContentSection overflows css={{ height: "100%" }}>
          {hasBreakingChanges ?
            <Body size="default" type="regular">
              You’ve added one or more required fields. Because existing records
              may not have data for these, we can’t safely update them. To apply
              changes only to new records moving forward, update below. To
              update existing records, cancel and make all fields optional.
            </Body>
          : <>
              <Body size="default" type="regular">
                You’ve made updates to this record type. Should these changes be
                updated on all existing records, or only to new ones going
                forward?
              </Body>
              <InputRow>
                <Radio
                  control={control}
                  name="promoteSubmissions"
                  options={[
                    { value: "true", text: "Update existing and new records" },
                    { value: "false", text: "Only update new records" },
                  ]}
                />
              </InputRow>
            </>
          }
        </ContentSection>
        <ButtonSection>
          <PrimaryButtons>
            <Button onClick={onCancel} size="medium" styleVariant="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(onSubmit)}
              size="medium"
              styleVariant="primary"
              type="button"
            >
              Update
            </Button>
          </PrimaryButtons>
        </ButtonSection>
      </FormSection>
    </Container>
  );
};
